/**
 * Light Mode Demo Page - Visual Preview and Approval
 *
 * Purpose: Showcase light mode theme for design approval before site-wide rollout.
 * Forces light theme regardless of user preference.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowLeft, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { ModelBadge } from '../components/results/shared/ModelBadge';

export function LightModeDemoPage() {
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  // Force light mode for this demo page
  useEffect(() => {
    setTheme('light');
    document.documentElement.setAttribute('data-theme', 'light');

    // Cleanup: restore theme on unmount
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [setTheme]);

  const handleExit = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Demo Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleExit}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              aria-label="Exit demo"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Exit Demo</span>
            </button>
            <div className="h-6 w-px bg-slate-300" />
            <h1 className="text-lg font-semibold text-slate-900">Light Mode Preview</h1>
          </div>
          <div className="text-sm text-slate-500">
            Design approval required before rollout
          </div>
        </div>
      </div>

      {/* Demo Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">

        {/* Typography Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Typography</h3>
          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-slate-900">Heading 1 - Main Page Title</h1>
            <h2 className="text-2xl font-semibold text-slate-800">Heading 2 - Section Title</h2>
            <h3 className="text-xl font-medium text-slate-700">Heading 3 - Subsection</h3>
            <p className="text-base text-slate-700">Body text - This is standard paragraph text with good readability.</p>
            <p className="text-sm text-slate-600">Small text - Secondary information and descriptions.</p>
            <p className="text-xs text-slate-500">Extra small text - Captions and metadata.</p>
            <a href="#" className="text-cyan-500 hover:text-cyan-600 underline">Link example - Maintains brand accent color</a>
          </div>
        </section>

        {/* Buttons Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Buttons</h3>
          <div className="flex flex-wrap gap-3">
            <button className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-lg hover:from-cyan-600 hover:to-cyan-700 font-medium transition-all">
              Primary Button
            </button>
            <button className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium border border-slate-300 transition-colors">
              Secondary Button
            </button>
            <button className="px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg font-medium transition-colors">
              Ghost Button
            </button>
            <button className="px-4 py-2 bg-slate-300 text-slate-500 rounded-lg cursor-not-allowed font-medium" disabled>
              Disabled Button
            </button>
          </div>
        </section>

        {/* Form Inputs Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Form Inputs</h3>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Text Input</label>
              <input
                type="text"
                placeholder="Enter text here"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Dropdown</label>
              <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white text-slate-900">
                <option>Option 1</option>
                <option>Option 2</option>
                <option>Option 3</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="demo-checkbox" className="w-4 h-4 text-cyan-500 border-slate-300 rounded focus:ring-cyan-500" />
              <label htmlFor="demo-checkbox" className="text-sm text-slate-700">Checkbox example</label>
            </div>
          </div>
        </section>

        {/* Cards Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Cards & Surfaces</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Standard Card</h4>
              <p className="text-sm text-slate-600">Card with subtle background and border for content grouping.</p>
            </div>
            <div className="bg-white border border-slate-300 rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-2">Elevated Card</h4>
              <p className="text-sm text-slate-600">Card with shadow for more prominence.</p>
            </div>
          </div>
        </section>

        {/* Model Badges Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Model Badges</h3>
          <div className="flex flex-wrap gap-3">
            <ModelBadge provider="openai" model="gpt-4" size="small" />
            <ModelBadge provider="anthropic" model="claude-3-opus" size="small" />
            <ModelBadge provider="google" model="gemini-pro" size="medium" />
          </div>
        </section>

        {/* Notifications Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Notifications & Alerts</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">Success Message</p>
                <p className="text-xs text-green-700">Operation completed successfully.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error Message</p>
                <p className="text-xs text-red-700">Something went wrong. Please try again.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Info Message</p>
                <p className="text-xs text-blue-700">Here's some helpful information.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Loading States Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Loading States</h3>
          <div className="flex items-center gap-6">
            <div className="w-8 h-8 border-3 border-slate-200 border-t-cyan-500 rounded-full animate-spin" />
            <div className="space-y-2 flex-1 max-w-xs">
              <div className="h-4 bg-slate-200 rounded animate-pulse" />
              <div className="h-4 bg-slate-200 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
            </div>
          </div>
        </section>

        {/* Modal & Toast Demo Section */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Interactive Components</h3>
          <div className="flex gap-3">
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium border border-slate-300"
            >
              Show Modal Example
            </button>
            <button
              onClick={() => {
                setToastVisible(true);
                setTimeout(() => setToastVisible(false), 3000);
              }}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium border border-slate-300"
            >
              Show Toast
            </button>
          </div>
        </section>

        {/* Sample Comparison Result Preview */}
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Comparison Results Preview</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-3">
                <ModelBadge provider="openai" model="gpt-4" size="small" />
                <span className="text-sm font-medium text-slate-700">Winner</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">Score: 9.2</div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-3">
                <ModelBadge provider="anthropic" model="claude-3-sonnet" size="small" />
              </div>
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 bg-slate-200 text-slate-700 text-xs font-semibold rounded">Score: 8.7</div>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Modal Overlay */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Modal Example</h3>
            <p className="text-slate-600 mb-4">This is how modals appear in light mode with proper contrast and shadows.</p>
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 font-medium w-full"
            >
              Close Modal
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastVisible && (
        <div className="fixed top-4 right-4 bg-white border border-slate-200 rounded-lg shadow-lg p-4 max-w-sm z-50 animate-in slide-in-from-top">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-900">Toast Notification</p>
              <p className="text-xs text-slate-600">This is how toast notifications look in light mode.</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
